import React, { useMemo } from 'react'
import { getTime } from 'date-fns'
import { omitBy, isUndefined } from '@golden/utils'
import { ActivityType, ActivityDailyBonusDrawStatusType } from '@golden/gdk-campaign'
import Grid from '@material-ui/core/Grid'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FormField from '../../default/form/FormField'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import { FormState, ValueGetter } from '../../../utils/default/FormHook'
import { ActivityFormType, ActivityRequest } from './ActivityRecordDetailForm'
import useT from '../../../i18ns/admin/useT'
import { getValueFromChangeEvent, convertEmptyToUndefined } from '../../../utils/default/FormHelper'
import { SearchToRequestFunc } from '../../../utils/default/ComplexFlowHook'
import { dailyBonusDrawStatusName } from '../../../constants/default/activityStatusName'

export interface ActivityDailyBonusDrawFormType {
  account: string
  agent_account: string
  dailyBonusDraw: ActivityDailyBonusDrawStatusType | '--'
}

export const initialForm = (): ActivityDailyBonusDrawFormType => ({
  account: '',
  agent_account: '',
  dailyBonusDraw: '--'
})

export const getValueFromEvent: ValueGetter<ActivityDailyBonusDrawFormType> = {
  account: getValueFromChangeEvent,
  agent_account: getValueFromChangeEvent,
  dailyBonusDraw: getValueFromChangeEvent
}

export const formToRequest = (form: ActivityFormType): ActivityRequest => {
  const converted = {
    activity: ActivityType.DAILY_BONUS_DRAW,
    account: convertEmptyToUndefined(form.account),
    agent_account: convertEmptyToUndefined(form.agent_account),
    status: form.dailyBonusDraw === '--' ? undefined : form.dailyBonusDraw,
    type: form.type,
    start_at: form.time.start === null ? undefined : getTime(form.time.start),
    end_at: form.time.end === null ? undefined : getTime(form.time.end),
    page: 1
  } as ActivityRequest
  return omitBy(converted, isUndefined) as ActivityRequest
}

export const searchToRequest: SearchToRequestFunc<{
  account: string | undefined
  agent_account: string | undefined
  status: ActivityDailyBonusDrawStatusType | '--'
}> = (search) => {
  const converted = {
    account: search.account ? search.account : (search.account ? search.account : undefined),
    agent_account: search.agent_account,
    dailyBonusDraw: search.status ? search.status : '--'
  }
  return omitBy(converted, isUndefined) as any
}

interface PropTypes {
  context: React.Context<FormState<ActivityFormType>>
}

const TextField = React.memo(MuiTextField)

const ActivityRecordDailyBonusDrawForm: React.FC<PropTypes> = (props) => {
  const { context } = props
  const { t } = useT()

  const stateOptions = useMemo(() => {
    return [{ name: t('common.all'), value: '--' }]
      .concat(
        Object.keys(dailyBonusDrawStatusName)
          .map((key) => ({ name: t(dailyBonusDrawStatusName[key as ActivityDailyBonusDrawStatusType]), value: key })) as any
      )
  }, [t])

  return (
    <React.Fragment>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="account"
          label={t('common.playerAccount')}
          placeholder={t('placeholder.inputPlayerAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, TextFieldProps>
          context={context}
          component={TextField}
          name="agent_account"
          label={t('common.agentAccount')}
          placeholder={t('placeholder.inputAgentAccount')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <FormField<ActivityFormType, DropDownProps>
          context={context}
          component={DropDown}
          name="dailyBonusDraw"
          label={t('common.status')}
          options={stateOptions}
          fullWidth
        />
      </Grid>
    </React.Fragment>
  )
}

export default React.memo(ActivityRecordDailyBonusDrawForm)
